import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Paper, Stack, Dialog, DialogTitle, DialogContent, IconButton, useMediaQuery } from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

import ChatBubbleRAG from './ChatBubbleRAG';
import MessageInput from './MessageInput';
import MessagesPaneHeader from './MessagesPaneHeader';
import { MessageProps } from '../types';
import { useMsal } from '@azure/msal-react';
import { RootState } from '../store/AriStore';
import { generateRandomString } from '../utils';
import { useTheme } from '@mui/material';
import { api } from '../api/streamFetch';

function formattedDate() {
  const formatter = new Intl.DateTimeFormat('ko-KR', options);
  const [{ value: month },,{ value: day },,{ value: hour },,{ value: minute }] = formatter.formatToParts(new Date());
  const formattedDate = `${month}.${day} ${hour}:${minute}`;

  return formattedDate;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const options: Intl.DateTimeFormatOptions = {
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

// function getStorageToken() {
//   const authService = new AuthService();
//   authService.getStorageAccessToken().then(token => {
//     return token;
//   });
// }

export default function MessagesPane() {
  const [open, setOpen] = useState(false);
  const [document, setDocument] = useState();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const onShowCitation = (citation: string, index: number) => {
    setDocument(citation as any);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
      setImageUrl(null);
    }
  };

  // getToken().then(token => {
  //   reqToken = token;
  // });

  // const storageToken = sessionStorage.getItem("storageAccessToken");

  const menuInfo = useSelector((state: RootState) => state.menu);

  const [chatMessages, setChatMessages] = useState<MessageProps[]>([{
    id: "DEFAULT",
    sender: 'ARI',
    account: null,
    content: menuInfo.selectedWelcome,
    timestamp: formattedDate(),
  }]);

  const [textAreaValue, setTextAreaValue] = useState('');
  const chatCompletionData = useSelector((state: any) => state.chat);
  const { accounts } = useMsal();

  const streamControllerRef = useRef<AbortController | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const [activeStreamId, setActiveStreamId] = useState<string | null>(null);

  const fetchData = async (signal?: AbortSignal) => {
    setChatMessages([
      ...chatMessages,
      {
        id: "DEFAULT",
        sender: 'ARI',
        account: null,
        content: '찾아보는 중..',
        timestamp: new Intl.DateTimeFormat('ko-KR', options).format(new Date())
      },
    ]);

    // const api_host = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : 'https://gsr-dt-api-p001-qa.azurewebsites.net';

    // const response = await fetch(menuInfo.selectedIndex !== 500 ? `${api_host}/stream/claude` : `${api_host}/stream/gpt`, {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //     'Authorization': `Bearer ${sessionStorage.getItem("idToken")}`,
    //   },
    //   credentials: 'include',
    //   mode: 'cors',
    //   body: JSON.stringify({ account: accounts[0]['username'], message: chatMessages, menuIndex: menuInfo.selectedIndex }),
    // });

    try {
      const response = await api.post(menuInfo.selectedIndex !== 500 ? `/stream/claude` : `/stream/gpt`, 
        { account: accounts[0]['username'], message: chatMessages, menuIndex: menuInfo.selectedIndex },
        { signal }
      );

      if (response.body) {
        const reader = response.body.getReader();
        readerRef.current = reader;
        const decoder = new TextDecoder();
        let msg = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log('Receive Stream Data Done!');
            break;
          }
          const chunk = decoder.decode(value, { stream: true });
          const newMessages = chunk.split('\n\n')
            .map(message => {
              return message.split('data:').join('');
            });
          // eslint-disable-next-line no-loop-func
          msg = msg + newMessages.join('');

          await sleep(33);

          setChatMessages(prev => {
            const last = prev[prev.length - 1];
            const content = msg ? msg : 'Loading..';
            if (last && last.sender === 'ARI') {
              const updated = [...prev];
              updated[updated.length - 1] = { ...last, content, timestamp: formattedDate() };
              return updated;
            }
            const newId = last
              ? last.id
              : "ARI:" + Math.random().toString(36).substring(2, 12);
            return [
              ...prev,
              {
                id: newId,
                sender: 'ARI',
                account: null,
                content,
                timestamp: formattedDate(),
              },
            ];
          });
        }
      }
    } catch (error: any) {
      if (error && error.name === 'AbortError') {
        return;
      }
      console.error('API 호출 중 오류 발생:', error);

      if (error) {
        if (error.status === 401) {
          // 인증 오류 처리
          setChatMessages(prev => [
            ...prev,
            {
              id: prev.length > 0 ? prev[prev.length - 1].id : "ARI:" + Math.random().toString(36).substring(2, 12),
              sender: 'ARI',
              account: null,
              content: '인증이 필요합니다. 다시 로그인해 주세요.',
              timestamp: formattedDate(),
            },
          ]);
          // 선택적으로 로그인 페이지로 리다이렉트
          // window.location.href = '/login';
        } else if (error.status === 403) {
          // 권한 오류 처리
          setChatMessages(prev => [
            ...prev,
            {
              id: prev.length > 0 ? prev[prev.length - 1].id : "ARI:" + Math.random().toString(36).substring(2, 12),
              sender: 'ARI',
              account: null,
              content: '해당 기능에 접근 권한이 없습니다. 관리자에게 문의하세요.',
              timestamp: formattedDate(),
            },
          ]);
        } else {
          // 기타 오류 처리
          setChatMessages(prev => [
            ...prev,
            {
              id: prev.length > 0 ? prev[prev.length - 1].id : "ARI:" + Math.random().toString(36).substring(2, 12),
              sender: 'ARI',
              account: null,
              content: '요청 처리 중 오류가 발생했습니다.',
              timestamp: formattedDate(),
            },
          ]);
        }
      } else {
        // 네트워크 오류 등
        setChatMessages(prev => [
          ...prev,
          {
            id: prev.length > 0 ? prev[prev.length - 1].id : "ARI:" + Math.random().toString(36).substring(2, 12),
            sender: 'ARI',
            account: null,
            content: '네트워크 오류가 발생했습니다. 연결 상태를 확인해 주세요.',
            timestamp: formattedDate(),
          },
        ]);
      }
    }
  };

  

  useEffect(() => {
    if (!activeStreamId) return;
    if (streamControllerRef.current) {
      try { streamControllerRef.current.abort(); } catch (e) {}
    }
    const controller = new AbortController();
    streamControllerRef.current = controller;
    fetchData(controller.signal);
    return () => {
      if (streamControllerRef.current) {
        try { streamControllerRef.current.abort(); } catch (e) {}
        streamControllerRef.current = null;
      }
      if (readerRef.current) {
        try { readerRef.current.cancel(); } catch (e) {}
        readerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStreamId]);

  useEffect(() => {
    setChatMessages([{
      id: "DEFAULT",
      sender: 'ARI',
      account: null,
      content: menuInfo.selectedWelcome,
      timestamp: formattedDate(),
    }])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuInfo.selectedIndex]);

  useEffect(() => {
    if (chatMessages[chatMessages.length - 1]?.sender !== 'ARI') {
      const newId = generateRandomString(30);
      setChatMessages([
        ...chatMessages,
        {
          id: newId,
          sender: chatCompletionData.messageType,
          account: accounts[0]['username'],
          content: chatCompletionData.content,
          timestamp: formattedDate(),
        },
      ]);
      setActiveStreamId(newId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatCompletionData.content]); // Removed dispatch from dependency array

  // Fetch PNG as Blob with Authorization headers and create an object URL
  useEffect(() => {
    if (!document) {
      return;
    }
    const isPng = typeof document === 'string' && (document as string).toLowerCase().endsWith('.png');
    if (!isPng) {
      return;
    }

    const controller = new AbortController();
    let objectUrl: string | null = null;

    const fetchImage = async () => {
      try {
        const token = sessionStorage.getItem('storageAccessToken');
        const res = await fetch(`https://ari-contents-f5f6avf7f8fga3ef.a01.azurefd.net/content/${document}`, {
          method: 'GET',
          headers: {
            // 'Authorization': token ? `Bearer ${token}` : '',
            'x-ms-version': '2020-04-08',
            'x-ms-date': new Date().toUTCString(),
            'referer': 'https://ari.gsrdt.com'
          },
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new Error(`Failed to load image: ${res.status}`);
        }
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      } catch (e: any) {
        if (e && e.name === 'AbortError') {
          return;
        }
        console.error(e);
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
        setImageUrl(null);
      }
    };

    fetchImage();

    return () => {
      controller.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [document]);

  return (
    <Paper
      key={menuInfo.selectedIndex} // Ensure re-render when chat prop changes
      sx={{
        height: { xs: '100dvh', lg: '100dvh' },
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.level1',
        pt: { xs: 'var(--Header-height)', sm: 0 },
      }}
    >
        <>
      <MessagesPaneHeader menuTitle={menuInfo.selectedName} />
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          px: 2,
          py: 3,
          overflowY: 'scroll',
          flexDirection: 'column-reverse',
          backgroundColor: '#F0F4F8',
        }}
      >
        <Stack spacing={2} justifyContent="flex-end">
          {chatMessages.map((message: MessageProps, index: number) => {
            const isYou = message.sender !== 'ARI';
            return (
              <Stack
                key={index}
                direction="row"
                spacing={2}
                flexDirection={isYou ? 'row-reverse' : 'row'}
              >
                <ChatBubbleRAG key={index} variant={isYou ? 'sent' : 'received'} {...message} onCitationClicked={c => onShowCitation(c, index)}/>
              </Stack>
            );
          })}
        </Stack>
      </Box>
      <MessageInput
        textAreaValue={textAreaValue}
        setTextAreaValue={setTextAreaValue}
        onSubmit={() => {
          const newId = generateRandomString(30);
          setChatMessages([
            ...chatMessages,
            {
              id: newId,
              sender: accounts[0]['name'],
              account: accounts[0]['username'],
              content: textAreaValue,
              timestamp: formattedDate(),
            },
          ]);
          setActiveStreamId(newId);
        }}
      />
      </>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={fullScreen}
        sx={{zIndex: 10002, 
            "& .MuiDialog-container": {
              "& .MuiPaper-root": {
                width: "100%",
                maxWidth: "900px",  // Set your width here
                "& .MuiDialogContent-root": {
                  padding: 0,
                },
              },
            },
          }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Reference
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          {document !== undefined && document !== "" ? (
            typeof document === 'string' && (document as string).toLowerCase().endsWith('.png') ? (
              imageUrl ? (
                <img src={imageUrl} alt="Reference" style={{ width: '100%', height: 'auto', display: 'block' }} />
              ) : null
            ) : (
              <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}>
                <Viewer 
                  plugins={[defaultLayoutPluginInstance]}
                  fileUrl={`https://ari-contents-f5f6avf7f8fga3ef.a01.azurefd.net/content/${document}`} 
                  httpHeaders={{
                    // 'Authorization': `Bearer ${sessionStorage.getItem("storageAccessToken")}`,
                    'Content-Type': 'application/pdf',
                    'x-ms-version': '2020-04-08',
                    "x-ms-date": new Date().toUTCString(),
                    'referer': 'https://ari.gsrdt.com'
                  }}
                />
              </Worker>
            )
          ) : null}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}